import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import './SettingsGear.scss';

interface SettingsGearProps {
  onLanguageChange?: (lang: string) => void;
  className?: string;
}

export const SettingsGear: React.FC<SettingsGearProps> = ({ onLanguageChange, className = '' }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isLoggedIn = typeof window !== 'undefined' && !!localStorage.getItem('token');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowPassword(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    onLanguageChange?.(lang);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError('');
    setPwdMsg('');
    if (newPassword.length < 6) {
      setPwdError(t('dashboard.settings.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError(t('dashboard.settings.passwordMismatch'));
      return;
    }
    setPwdLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPwdMsg(t('dashboard.settings.passwordUpdated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwdError(err?.response?.data?.error || t('dashboard.settings.passwordFailed'));
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className={`settings-gear ${className}`} ref={ref}>
      <button
        type="button"
        className="settings-gear__btn"
        onClick={() => setOpen(!open)}
        aria-label={t('dashboard.settings.title')}
        title={t('dashboard.settings.title')}
      >
        <span className="settings-gear__icon">⚙</span>
      </button>
      {open && (
        <div className="settings-gear__dropdown">
          <div className="settings-gear__dropdown-title">{t('dashboard.settings.language')}</div>
          <div className="settings-gear__dropdown-options">
            <button type="button" className={i18n.language === 'ru' ? 'active' : ''} onClick={() => handleLang('ru')}>Русский</button>
            <button type="button" className={i18n.language === 'en' ? 'active' : ''} onClick={() => handleLang('en')}>English</button>
            <button type="button" className={i18n.language === 'es' ? 'active' : ''} onClick={() => handleLang('es')}>Español</button>
          </div>

          {isLoggedIn && (
            <>
              <div className="settings-gear__divider" />
              {!showPassword ? (
                <button
                  type="button"
                  className="settings-gear__action"
                  onClick={() => {
                    setShowPassword(true);
                    setPwdError('');
                    setPwdMsg('');
                  }}
                >
                  {t('dashboard.settings.changePassword')}
                </button>
              ) : (
                <form className="settings-gear__pwd-form" onSubmit={handleChangePassword}>
                  <div className="settings-gear__dropdown-title">{t('dashboard.settings.changePassword')}</div>
                  <input
                    type="password"
                    placeholder={t('dashboard.settings.currentPassword')}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <input
                    type="password"
                    placeholder={t('dashboard.settings.newPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <input
                    type="password"
                    placeholder={t('dashboard.settings.confirmPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  {pwdError && <p className="settings-gear__pwd-error">{pwdError}</p>}
                  {pwdMsg && <p className="settings-gear__pwd-ok">{pwdMsg}</p>}
                  <button type="submit" className="settings-gear__action settings-gear__action--primary" disabled={pwdLoading}>
                    {pwdLoading ? t('dashboard.settings.saving') : t('dashboard.settings.savePassword')}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsGear;
